/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { jsx } from '@compiled/react';

import { fg } from '@atlaskit/platform-feature-flags';
import { type ContentProps, Popup, type TriggerProps } from '@atlaskit/popup';

import { MenuControlContext } from '../context';
import { useUniversalCreateAnalytics } from '../hooks';
import type { CreateContentProps, UniversalCreateButtonProps } from '../types';
import type { UniversalCreateButtonPropsV2 } from '../types/buttonV2';
import { findSelectedItem, findSelectedItemV2, useNormalizeItems } from '../utils';

import { MenuContent, MenuWrapper, TopLevelWrappers, TriggerButton } from './components';
import AnalyticsWrapper from './components/AnalyticsWrapper';
import { MenuContentV2 } from './components/MenuContentV2';
import { useIsResponsiveView } from './utils';

// ============================================================================
// Type Definitions for Combined Props Support
// ============================================================================

/**
 * Combined props type that supports both v1 and v2 APIs.
 * Uses discriminated union based on the presence of 'items' (v1) or 'sections' (v2).
 */
type UniversalCreateButtonCombinedProps = UniversalCreateButtonProps | UniversalCreateButtonPropsV2;

/**
 * Type guard to check if props are v1 (has 'items' property)
 */
function isV1Props(props: UniversalCreateButtonCombinedProps): props is UniversalCreateButtonProps {
	return 'items' in props;
}

/**
 * Type guard to check if props are v2 (has 'sections' property)
 */
function isV2Props(
	props: UniversalCreateButtonCombinedProps,
): props is UniversalCreateButtonPropsV2 {
	return 'sections' in props;
}

function UniversalCreateButtonInner(props: UniversalCreateButtonCombinedProps) {
	// ============================================================================
	// Props Extraction with Version Detection
	// ============================================================================

	// Extract common props (available in both v1 and v2)
	const {
		children,
		testId,
		shouldRenderToParent,
		onClick,
		onClose,
		selectedItem,
		product,
		subProduct,
		isOpen: controlledOpen = false,
		enableTriggerPulse = false,
		triggerButtonSpotlightTarget,
		isNav4Enabled = true,
		buttonTheme,
		preventClose = false,
		onMouseEnter,
		onMouseLeave,
		primaryActionItem,
	} = props;

	// Extract version-specific props with type narrowing
	// V1 props
	const items = isV1Props(props) ? props.items : undefined;
	const suggestionConfig = isV1Props(props)
		? (props.suggestionConfig ?? ({ enabled: false } as const))
		: ({ enabled: false } as const);

	// V2 props
	const isV2Mode = isV2Props(props);
	const sections = useMemo(
		() => (isV2Mode ? props.sections : []),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[isV2Mode, isV2Mode && props.sections],
	);
	const crossAppCreationConfig = isV2Mode
		? (props.crossAppCreationConfig ?? ({ enabled: false } as const))
		: ({ enabled: false } as const);

	// ============================================================================
	// Component Logic (unchanged from original)
	// ============================================================================
	const isResponsive = useIsResponsiveView();
	const [isOpen, setIsOpen] = useState(controlledOpen || false);

	// Normalize items for v1, or use empty array for v2
	const structuredItems = useNormalizeItems(items ?? []);

	const defaultMenu = useCallback(
		() => (isV2Mode ? <MenuContentV2 /> : <MenuContent />),
		[isV2Mode],
	);
	const [createContent, setCreateContent] = useState<(contentProps: ContentProps) => ReactNode>(
		() => defaultMenu,
	);
	const { fireMenuStateChanged } = useUniversalCreateAnalytics();

	const reset = useCallback(() => {
		setCreateContent(() => defaultMenu);
	}, [defaultMenu]);

	const close = useCallback(() => {
		setCreateContent(() => defaultMenu);
		setIsOpen(false);
	}, [defaultMenu]);

	const load = useCallback(
		(loadCreateContent: CreateContentProps) => {
			setCreateContent(
				() => (contentProps: ContentProps) =>
					loadCreateContent(contentProps, {
						reset,
						close,
					}),
			);
		},
		[close, reset],
	);

	const triggerButtonClick = useCallback(() => {
		setIsOpen((prev) => {
			if (prev) {
				onClose?.();
			}
			return !prev;
		});

		onClick?.();
	}, [onClick, onClose, setIsOpen]);

	const popupTrigger = useCallback(
		(triggerProps: TriggerProps) => (
			<TriggerButton
				testId={testId}
				triggerButtonSpotlightTarget={triggerButtonSpotlightTarget}
				enableTriggerPulse={enableTriggerPulse}
				isOpen={isOpen}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				onClick={triggerButtonClick}
				triggerProps={triggerProps}
				isNav4Enabled={isNav4Enabled}
				buttonTheme={buttonTheme}
				primaryActionItem={primaryActionItem}
			>
				{children}
			</TriggerButton>
		),
		[
			children,
			enableTriggerPulse,
			isOpen,
			testId,
			triggerButtonClick,
			triggerButtonSpotlightTarget,
			buttonTheme,
			isNav4Enabled,
			onMouseEnter,
			onMouseLeave,
			primaryActionItem,
		],
	);

	const onCloseMenu = useCallback(() => {
		if (product === 'confluence' && preventClose) {
			return;
		}

		setIsOpen(false);
		setCreateContent(() => defaultMenu);
		onClose?.();
	}, [defaultMenu, onClose, product, preventClose]);

	const foundItem = useMemo(() => {
		if (!selectedItem) {
			return null;
		}
		if (isV2Mode) {
			return findSelectedItemV2(sections, selectedItem);
		}
		return findSelectedItem(structuredItems, selectedItem);
	}, [selectedItem, isV2Mode, sections, structuredItems]);

	useEffect(() => {
		if (isOpen) {
			const loadContent = foundItem?.loadCreateContent;
			if (loadContent) {
				setCreateContent(
					() => (contentProps: ContentProps) =>
						loadContent(contentProps, {
							reset,
							close,
						}),
				);
			} else {
				reset();
			}
		}
	}, [close, isOpen, foundItem?.loadCreateContent, reset]);

	useEffect(() => {
		if (controlledOpen !== undefined) {
			setIsOpen(controlledOpen);
		}
	}, [controlledOpen]);

	const prevIsOpenRef = useRef<boolean>(isOpen);

	useEffect(() => {
		const prevIsOpen = prevIsOpenRef.current;

		// Only fire analytics on actual state transitions
		if (isOpen !== prevIsOpen) {
			fireMenuStateChanged({
				itemState: isOpen ? 'expanded' : 'collapsed',
				version: isV2Mode ? 'v2' : 'v1',
			});
		}
		prevIsOpenRef.current = isOpen;
	}, [isOpen, fireMenuStateChanged, prevIsOpenRef, isV2Mode]);

	const loadPopupContent = useCallback(
		(props: ContentProps) => <MenuWrapper>{createContent(props)}</MenuWrapper>,
		[createContent],
	);

	const popup = (
		<Popup
			isOpen={isOpen}
			placement={
				(isResponsive && isNav4Enabled) || primaryActionItem ? 'bottom-end' : 'bottom-start'
			}
			// eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent
			shouldRenderToParent={shouldRenderToParent}
			onClose={onCloseMenu}
			content={loadPopupContent}
			trigger={popupTrigger}
		/>
	);

	// ============================================================================
	// MenuControlContext Value Construction
	// Populate context with appropriate values based on props version
	// ============================================================================

	const menuControlContextValue = {
		reset,
		close,
		load,
		// V1 fields
		mixedItems: structuredItems,
		suggestionConfig: suggestionConfig ?? { enabled: false },
		// V2 fields
		sections,
		crossAppCreationConfig: crossAppCreationConfig ?? { enabled: false },
		// Common fields
		product,
		subProduct,
		isNav4Enabled,
	};

	return (
		<MenuControlContext.Provider value={menuControlContextValue}>
			<TopLevelWrappers>{popup}</TopLevelWrappers>
		</MenuControlContext.Provider>
	);
}

export function UniversalCreateButton(props: UniversalCreateButtonCombinedProps) {
	if (fg('navx-2236-unified-instrumentation-updates')) {
		return (
			<AnalyticsWrapper>
				<UniversalCreateButtonInner {...props} />
			</AnalyticsWrapper>
		);
	}

	return <UniversalCreateButtonInner {...props} />;
}

export default UniversalCreateButton;
